import { dataApi } from '@/services/dataApi'
import { toModel } from '@/services/normalizer'
import dateTimeUtils from '@/utils/date-time-utils'
import displayUtils from '@/utils/display-utils'
import fileUtils from '@/utils/file-utils'
import numberUtils from '@/utils/number-utils'
import objectUtils from '@/utils/object-utils'
import stringUtils from '@/utils/string-utils'
import fieldRuleUtils from '@/utils/field-rule-utils'

// Load RouteLeaf app base url
const ROUTELEAF_APP_BASE_URL = process.env.VUE_APP_ROUTELEAF_APP_BASE_URL

// Fallback for browsers without the Clipboard API. Explicit focus and selection
// are required by iOS Safari before it will honor execCommand('copy').
const copyTextUsingExecCommand = (text) => {
  if (typeof document === 'undefined' || !document.body) return false

  const element = document.createElement('textarea')
  const selection = document.getSelection()
  const selectedRange = selection && selection.rangeCount > 0
    ? selection.getRangeAt(0)
    : null
  const activeElement = document.activeElement

  element.value = text
  element.setAttribute('readonly', '')
  element.setAttribute('aria-hidden', 'true')
  element.setAttribute('tabindex', '-1')
  element.style.position = 'absolute'
  element.style.left = '-9999px'
  element.style.top = `${window.pageYOffset || document.documentElement.scrollTop}px`
  element.style.fontSize = '16px'

  document.body.appendChild(element)

  let copied = false
  try {
    element.focus()
    element.setSelectionRange(0, text.length)
    copied = document.execCommand('copy')
  } catch (err) {
    console.error('Clipboard fallback failed', err)
  } finally {
    document.body.removeChild(element)

    if (selectedRange && selection) {
      selection.removeAllRanges()
      selection.addRange(selectedRange)
    }
    if (activeElement && typeof activeElement.focus === 'function') activeElement.focus()
  }

  return copied
}

export default {
  // ---------------------------------------------------------------------------
  // Inject data => reference as: this.$root.<data>
  // ---------------------------------------------------------------------------
  data: {
    // The RouteLeaf Data API (http service, without auth header)
    dataApi,

    // The app model normalizer
    toModel,

    // Field rules
    rules: { ...fieldRuleUtils },

    // A useful set of general utility functions
    utils: { ...dateTimeUtils, ...displayUtils, ...fileUtils, ...numberUtils, ...objectUtils, ...stringUtils },

    // The allowed file types for all upload scenarios
    allowedFileTypesForUploads: 'application/pdf, image/jpeg, image/png, image/webp'
  },

  // ---------------------------------------------------------------------------
  // Inject functions => reference as: this.$root.<method>
  // ---------------------------------------------------------------------------
  methods: {
    // --------------------------------------------------- Global App Operations

    // Returns the UI type of the login (the audience: shipper, receiver, sys-admin)
    uiType () {
      const activeOrg = this.$store.getters['auth/activeOrg']
      return activeOrg?.org_type_code
    },

    // Returns the base URL for the app
    appBaseUrl () {
      return ROUTELEAF_APP_BASE_URL
    },

    // Determines if the viewport is mobile size
    isMobileView () {
      return (window && window.innerWidth && window.innerWidth < 600)
    },

    // Determines if the viewport is tablet size
    isTabletView () {
      return (window && window.innerWidth && window.innerWidth <= 1024)
    },

    // Toggles the main navigation menu
    toggleMainNav () {
      this.$children[0].toggleMainNav()
    },

    // Returns true if the main nav menu is open
    isMainNavOpen () {
      return this.$children[0].isNavOpen
    },

    // Returns true if the active user has the specified role
    activeUserHasRole (role) {
      const activeUser = this.$store.getters['auth/activeUser']
      return objectUtils.includes(activeUser.roles, role)
    },

    // Convenience function for changing to a named route
    goTo (routeName, params = {}) {
      if (routeName && this.$router.currentRoute.name !== routeName) {
        return this.$router.push({ name: routeName, params })
      }
      return false
    },

    // Direct browser to last page
    goToLastPage () {
      this.$router.back()
    },

    // Save data to the clipboard, with an iOS-compatible legacy fallback.
    copyToClipboard (data = '') {
      const text = String(data)
      const fallback = () => {
        const copied = copyTextUsingExecCommand(text)
        if (!copied) console.error('Unable to copy text to the clipboard')
        return copied
      }

      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function' &&
        window.isSecureContext
      ) {
        return navigator.clipboard.writeText(text)
          .then(() => true)
          .catch((err) => {
            console.warn('Clipboard API failed; trying fallback', err)
            return fallback()
          })
      }

      return Promise.resolve(fallback())
    },

    // NOTE - This is just temporary until we allow an Org to manage their own settings !!!
    // Returns the active order pickup wait time for route estimations (in mins).
    getOrderPickupWaitTime (orgId) {
      return 5
    },

    // NOTE - This is just temporary until we allow an Org to manage their own settings !!!
    // Returns the active order dropoff wait time for route estimations (in mins)
    getOrderDropoffWaitTime (orgId) {
      return 12
    },

    // Returns the display name for the provider alias
    providerAliasToName (alias) {
      switch (alias) {
        case 'leaflink': return 'LeafLink'
        case 'leaftrade': return 'LeafTrade'
        case 'biotrack': return 'BioTrack'
        default: return alias
      }
    },

    // Returns the thirdparty invoice url for an order.
    // Supports both ProviderOrder & Order as the parameter.
    providerInvoiceUrl (order = {}, providerConnection = {}) {
      let url = null
      const provider = order.provider_alias
      const orderId = order.thirdparty_order_id
      const accountAlias = providerConnection?.account?.alias
      if (!provider || !orderId) return url

      switch (provider) {
        case 'leaflink': {
          const providerBaseUrl = process.env.VUE_APP_PROVIDER_LEAFLINK_ASSETS_BASE_URL
          if (providerBaseUrl && accountAlias) url = `${providerBaseUrl}/c/${accountAlias}/${orderId}/invoice`
        }
      }

      return url
    },

    // Returns a signed access URL for viewing a user asset
    // If no userId provided, assumes the actively authenticated user
    async fetchUserAsset (assetKey, userId) {
      if (!assetKey) return false

      const uri = (userId) ? `user/${userId}/asset/accessUrl` : 'my/user/asset/accessUrl'

      // Check if asset exists
      const accessInfo = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri,
        opts: {
          params: { asset_key: assetKey }
        },
        to_model: false
      })

      // Return the access url
      if (accessInfo.url) return accessInfo.url
      else return null
    },

    // Returns a signed access URL for viewing a location asset
    async fetchLocationAsset (locationId, assetKey) {
      if (!locationId || !assetKey) return false

      // Check if asset exists
      const accessInfo = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `location/${locationId}/asset/accessUrl`,
        opts: {
          params: { asset_key: assetKey }
        },
        to_model: false
      })

      // Return the access url
      if (accessInfo.url) return accessInfo.url
      else return null
    },

    // Returns a signed access URL for viewing an order asset
    async fetchOrderAsset (orderId, assetKey, inline = false) {
      if (!orderId || !assetKey) return false

      // Obtain access url
      const params = { asset_key: assetKey }
      if (inline === true) params.inline = true
      const accessInfo = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `order/${orderId}/asset/accessUrl`,
        opts: { params },
        to_model: false
      })

      // Check if asset exists, and return the access url
      const response = await fetch(accessInfo.url, { method: 'GET' })
      const url = (response.status === 200)
        ? accessInfo.url
        : null

      return url
    },

    // Returns a signed access URL for viewing a route asset
    async fetchRouteAsset (routeId, assetKey) {
      if (!routeId || !assetKey) return false

      // Obtain access url
      const accessInfo = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `route/${routeId}/asset/accessUrl`,
        opts: {
          params: { asset_key: assetKey }
        },
        to_model: false
      })

      // Check if asset exists, and return the access url
      const response = await fetch(accessInfo.url, { method: 'GET' })
      const url = (response.status === 200)
        ? accessInfo.url
        : null

      return url
    },

    // Determines if order invoice of provided order is guaranteed to be accurate
    // with respect to Driver and FA card information (i.e. post invoice pre-render).
    isOrderInvoiceGuaranteed (order = {}) {
      let result = false
      const preRenderDate = '2024-03-15' // March 15, 2024
      if (order.placed_at) {
        result = new Date(order.placed_at) >= new Date(preRenderDate)
      }
      return result
    },

    // Trigger order invoice downloads (via download manager)
    // Supports multiple orders
    sendDownloadOrderInvoiceRequest (orders = []) {
      this.$children[0].sendOrderInvoiceRequestToDownloadManager(orders)
    },

    // Trigger order attachment docs downloads (via download manager)
    // Supports a single order
    sendDownloadOrderAttachmentsRequest (order = {}) {
      this.$children[0].sendOrderAttachmentsRequestToDownloadManager(order)
    },

    // Closes the download manager component
    closeDownloadManager () {
      this.$children[0].closeDownloadManager()
    },

    // Invokes the "system message" dialog with a provided message to display
    showSystemMessage (message = '') {
      this.$children[0].revealSystemMessage(message)
    },

    // Invokes the "system message" dialog with the "System is Down" message
    showSystemDownMessage () {
      const message = 'The RouteLeaf system is not available.'
      this.$children[0].revealSystemMessage(message)
    },

    // Convenience function for handling login
    async login (creds) {
      return this.$store.dispatch('auth/loginUser', creds)
    },

    // Convenience function for handling logout, and pushing back to the login page
    async logout () {
      await this.$store.dispatch('auth/logoutUser')
      this.$store.dispatch('sh/clearData', { editing: true }) // clear all data
      this.goTo('login') // direct user to login page
    }
  }
}
